import { X } from 'lucide-react';
import type { Room } from '../types';

interface MotorcycleOwnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  rooms: Room[];
  onRoomClick?: (roomNumber: string) => void;
}

export function MotorcycleOwnersModal({ isOpen, onClose, rooms, onRoomClick }: MotorcycleOwnersModalProps) {
  if (!isOpen) return null;

  // Get all residents with motorcycle plates
  const residentsWithMotorcycle = rooms.flatMap(room =>
    room.residents
      .filter(resident => resident.motorcyclePlate && resident.motorcyclePlate.trim() !== '')
      .map(resident => ({
        name: resident.name,
        roomNumber: room.number,
        motorcyclePlate: resident.motorcyclePlate
      }))
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
            🏍️ Penghuni dengan Motor
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {residentsWithMotorcycle.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                Belum ada penghuni yang mendaftarkan motor
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Total: <span className="font-semibold text-gray-900 dark:text-gray-100">{residentsWithMotorcycle.length}</span> penghuni
              </p>

              {/* List of residents with motorcycle */}
              <div className="grid gap-3">
                {residentsWithMotorcycle.map((resident, index) => (
                  <div
                    key={`${resident.roomNumber}-${resident.name}-${index}`}
                    className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer border border-gray-200 dark:border-gray-700"
                    onClick={() => {
                      if (onRoomClick) {
                        onRoomClick(resident.roomNumber);
                        onClose();
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Kamar {resident.roomNumber}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 truncate">
                          {resident.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Plat:</span>
                          <span className="font-mono font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600">
                            {resident.motorcyclePlate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}
